// source-overrides.ts — the client-only display overlay BotSources.tsx applies on top of
// whatever `GET /sources` last returned, for its resync/toggle-rendered-mode mutations. Pure
// and tested per the dashboard's "logic that can be tested lives in src/lib" rule (no
// component-test harness for this file — `BotSources.test.tsx` exists ONLY to prove `deleting`
// needs no such harness's help, see its own module comment) — this is the one part of that
// screen with real branching.
//
// Why an overlay instead of a one-shot optimistic cache write: resync and toggle-rendered-mode
// both enqueue a job and return before the job poller (≤2s tick, apps/api/src/jobs/poller.ts)
// ever claims it — so ANY refetch that lands before the job actually runs, whether it's the
// screen's own `refetchInterval`, an explicit invalidate, or React Query's default
// refetch-on-window-focus firing because the owner alt-tabbed away and back right after
// clicking, legitimately returns server state that hasn't moved yet and would silently undo a
// one-shot optimistic cache write. Verified live: a fresh fetch within ~1s of a resync click
// reliably still reflects the pre-mutation state, and nothing further ever refetches once that
// stale fetch turns `ANY_SYNCING` back off. An overlay applied fresh to every fetch result —
// rather than a single cache mutation that the next fetch can clobber — survives that
// regardless of which fetch races it.
//
// Task 4 (source-detail-diagnostics) removed this file's `'deleted'` override entirely.
// `DELETE /sources/:sid` (Task 3) is now an atomic compare-and-swap that returns the real,
// post-write `SourceDto` (`status: 'deleting'`) directly — there is nothing left for the client
// to mask or guess at. `deleting` is a genuine `SourceStatus` the server can report on any
// later fetch too, so it needed exactly the same treatment `error`/`ready`/etc. already get:
// added to `ANY_SYNCING` below (so polling doesn't stop mid-teardown) and rendered by
// `StatusChip` (already done, `packages/ui/src/components/status-chip.tsx`) — no override
// required.
import type { SourceDto, SourceStatus } from '@repo/shared-types';

export const ANY_SYNCING: SourceStatus[] = ['pending', 'crawling', 'indexing', 'deleting'];

/** The row's real, observed pre-mutation state — NOT an assumed one. Fix-round-1 review
 * (Critical): the first version hardcoded `status: 'ready'` as "the" pre-mutation status, but
 * Resync and the rendered-mode toggle are both clickable from `error` too — `syncing` only
 * covers `pending`/`crawling`/`indexing`, so `error` is a perfectly normal, non-disabled state
 * for that switch to be flipped from. And `error` isn't an edge case here: the near-empty-pages
 * hint (`crawl-source.ts`'s `renderedModeHint`, stored as `status: 'error'` with a `hint:`
 * message) exists specifically to tell the owner to flip this exact switch. Hardcoding `'ready'`
 * meant `isOverrideActive` returned `false` on the very first render for that entire path — the
 * chip never went to `pending`, `shouldKeepSyncing` never started polling, and the row sat on
 * the stale `error` until an incidental refetch: precisely the bug this override exists to
 * kill, on the path most likely to be used. Comparing against whatever the row actually was
 * (captured by the caller in `onMutate`) fixes this generically — there is no fixed list of
 * "allowed" starting statuses to maintain as the API grows new ones. */
export interface SourceOverrideBaseline {
  status: SourceStatus;
  lastSyncedAt: string | null;
  error: string | null;
}

export interface SourceOverride {
  status: 'pending';
  baseline: SourceOverrideBaseline;
  renderedMode?: boolean;
  startedAt: number;
}

/** How long a syncing override is allowed to keep forcing its `pending` DISPLAY purely by
 * matching its baseline, before that specific rendering choice is retired. Exists for one
 * specific case, found live while verifying the fix-round-1 Critical fix from an `error` row: a
 * resync/toggle that fails again with the EXACT same `status`/`lastSyncedAt`/`error` as before
 * it ran (a source pointed at a URL that's unreachable both times, for instance — reproduced
 * with a `.invalid` TLD, which failed with the identical zero-pages message under both the
 * static and CF renderer) makes the baseline comparison match forever, even though the job
 * genuinely ran and finished. Set well above the job's normal cadence (poller ticks every ≤2s;
 * a crawl + index typically finishes in single-digit seconds) so it never fires on the path
 * that's actually converging.
 *
 * Fix-round-2 review (Important): this deadline governs ONE thing only — whether
 * `applySourceOverrides` is still allowed to force the `pending` chip. It must NEVER be used to
 * decide whether to keep polling (`shouldKeepSyncing`), which is a separate question with a
 * separate, correct answer: keep asking the server for as long as the real data still hasn't
 * disagreed with the baseline, with no deadline at all. The first version of this file
 * conflated the two — `isOverrideActive` returned `false` on the deadline unconditionally,
 * before comparing to real data, so `shouldKeepSyncing` had nothing left holding it true once
 * that fired and `refetchInterval` gave up. That's reachable, not theoretical:
 * `apps/api/src/jobs/poller.ts` claims at most 5 jobs per 2s tick, ordered by `runAfter`, and
 * runs them sequentially with a `running` guard that blocks the next tick's claim until the
 * batch finishes — a burst of resyncs, one large-sitemap crawl, or a few sources touched
 * together can easily push a specific job's claim past 30s. Under the old code, when that
 * happened: polling stopped, and the chip froze on the stale pre-mutation status with nothing
 * left to unstick it — the original bug back, gated behind "queue backlog" instead of "any
 * click." Now, past this deadline, the chip stops pretending to know the answer and falls back
 * to whatever the last real fetch actually said (which may itself still be the stale baseline,
 * displayed honestly rather than masked as `pending`) — polling keeps going regardless, and
 * corrects the display the moment real data moves.
 *
 * Exported so `BotSources.tsx` can schedule a `setTimeout` at this same duration that forces a
 * re-render without touching the override's own content (see its own comment) — needed because
 * React Query's default `structuralSharing` means a background refetch that returns
 * byte-identical JSON reuses the SAME array reference rather than a new one, so a `useMemo`
 * keyed on that query's `data` never recomputes on its own once the data stops changing, and
 * this deadline would otherwise never get re-checked with a fresh `now`. */
export const OVERRIDE_TIMEOUT_MS = 30_000;

/** Whether `override` still needs the SERVER asked again — i.e. the most recent fetch hasn't
 * yet proven the job ran. Unresolved for as long as the row is still exactly the snapshot
 * captured right before the mutation (`status`, `lastSyncedAt`, AND `error` all unchanged from
 * `baseline`) — the moment a fetch shows the job actually moved the row (any one of those three
 * differs), it's resolved and the real data shows through on its own. Deliberately has NO time
 * limit (see `OVERRIDE_TIMEOUT_MS`'s doc comment for why that matters) — this is the single
 * source of truth `shouldKeepSyncing` polls against, and giving up on asking the server is a
 * different, worse decision than giving up on a specific rendering choice. Deliberately
 * stateless otherwise (no stored "is this settled yet" flag to maintain) — every consumer just
 * re-derives it from whatever fetch it's looking at; `overrides` state itself is never pruned. */
export function isOverrideUnresolved(source: SourceDto | undefined, override: SourceOverride): boolean {
  if (!source) return true;
  return (
    source.status === override.baseline.status &&
    source.lastSyncedAt === override.baseline.lastSyncedAt &&
    source.error === override.baseline.error
  );
}

/** Whether a syncing override should still force its `pending` display right now — unresolved
 * (see `isOverrideUnresolved`) AND within `OVERRIDE_TIMEOUT_MS` of `startedAt`. DISPLAY-ONLY:
 * `shouldKeepSyncing` deliberately does not use this. */
function isOverrideDisplayActive(
  source: SourceDto | undefined,
  override: SourceOverride,
  now: number,
): boolean {
  if (!isOverrideUnresolved(source, override)) return false;
  return now - override.startedAt <= OVERRIDE_TIMEOUT_MS;
}

/** Re-applies `overrides` on top of a freshly fetched sources list — see `isOverrideDisplayActive`
 * for when an entry still forces its display. An entry that isn't currently display-active is
 * passed through UNCHANGED, including its real `renderedMode` — fix-round-1 review (Important):
 * an earlier version applied the target `renderedMode` unconditionally, outside the active
 * check, so a settled override never let go of it. If the same source's rendered mode changed
 * elsewhere (another tab, a teammate) after this override settled, this tab would keep showing
 * the stale target value indefinitely with no way back short of a remount — directly
 * contradicting this function's own contract that a settled entry is a no-op. Settled entries
 * ARE now true no-ops, which is what makes leaving them in `overrides` state (never pruned)
 * harmless — note that "settled" here means display-settled; `shouldKeepSyncing` may still be
 * correctly polling for the exact same entry past its display deadline. */
export function applySourceOverrides(
  sources: SourceDto[],
  overrides: Record<string, SourceOverride>,
  now: number = Date.now(),
): SourceDto[] {
  const result: SourceDto[] = [];
  for (const source of sources) {
    const override = overrides[source.id];
    if (!override) {
      result.push(source);
      continue;
    }
    if (!isOverrideDisplayActive(source, override, now)) {
      result.push(source);
      continue;
    }
    result.push({
      ...source,
      status: 'pending',
      error: null,
      ...(override.renderedMode !== undefined ? { renderedMode: override.renderedMode } : null),
    });
  }
  return result;
}

/** Whether the sources list — after `overrides` are accounted for — should keep polling.
 * `ANY_SYNCING` alone isn't enough: without also checking still-unresolved overrides, a stray
 * refetch that raced a job back to 'ready' would turn polling back off before a later fetch
 * ever got real syncing data to react to. Deliberately built on `isOverrideUnresolved`, NOT
 * `isOverrideDisplayActive` — see `OVERRIDE_TIMEOUT_MS`'s doc comment for why polling must never
 * be time-boxed the way the display is. */
export function shouldKeepSyncing(
  sources: SourceDto[] | undefined,
  overrides: Record<string, SourceOverride>,
): boolean {
  const rawSyncing = sources?.some((s) => ANY_SYNCING.includes(s.status)) ?? false;
  const overlayUnresolved = Object.entries(overrides).some(([id, o]) =>
    isOverrideUnresolved(sources?.find((s) => s.id === id), o),
  );
  return rawSyncing || overlayUnresolved;
}
